import { z } from 'zod';
import { paginationQuerySchema } from '../common/pagination.js';
import { requisitionStatusSchema } from './status.js';
const idRef = z.string().regex(/^\d+$/, 'Identifikator raqamlardan iborat bo\'lishi kerak');
/**
 * So'ralayotgan miqdor — `numeric(14,3)`. Kasr mumkinligi BIRLIKKA bog'liq
 * (`DONA` da yo'q), lekin birlik so'rovda kelmaydi — u nomenklaturadan
 * o'qiladi, shuning uchun bu tekshiruv xizmatda (`assertQtyForUnit`).
 */
const qtySchema = z
    .number()
    .positive('Miqdor musbat bo\'lishi kerak')
    .max(99_999_999_999, 'Miqdor juda katta');
/**
 * Bir talabnomadagi satr chegarasi.
 *
 * Kirimdan (1000) kichikroq: talabnoma — odam qo'lda yozadigan hujjat, uni
 * rahbar ekranda o'qib tasdiqlaydi. Ming satrli talabnomani hech kim o'qimaydi,
 * ya'ni chegara faqat texnik emas, tashkiliy ham.
 */
export const MAX_REQUISITION_LINES = 200;
export const requisitionLineInputSchema = z.object({
    nomenclatureId: idRef,
    requestedQty: qtySchema,
    note: z.string().max(500).optional(),
});
/**
 * Talabnoma yaratish.
 *
 * `mtuId` YO'Q va bo'lmasligi ham kerak: u OMBORDAN ko'chiriladi. Mijoz aytgan
 * MTU ga ishonilsa, doira tekshiruvi mijozning qo'liga o'tib qolardi.
 */
export const createRequisitionSchema = z.object({
    warehouseId: idRef,
    purpose: z
        .string()
        .min(3, 'Maqsad kamida 3 belgidan iborat bo\'lishi kerak')
        .max(500, 'Maqsad 500 belgidan oshmasligi kerak'),
    /**
     * «Через кого» — mahsulotni olib ketadigan odam (ФМУ №25 blankidagi chiziq).
     *
     * Savolga javob ariza yozayotgan odamda bor: u kimni yuborishini biladi.
     * Blankda esa bu chiziq qo'lda emas, hujjatning O'ZIDAN to'lishi kerak.
     *
     * MATN, foydalanuvchiga havola emas: mahsulotni ko'pincha tizimda hisobi
     * yo'q odam olib ketadi — haydovchi, ishchi. Berish dalolatnomasidagi
     * qabul qiluvchining nomi ham xuddi shu sababdan matn.
     */
    throughPerson: z.string().min(2).max(200).optional(),
    neededBy: z.coerce.date().optional(),
    note: z.string().max(500).optional(),
    lines: z
        .array(requisitionLineInputSchema)
        .min(1, 'Talabnomada kamida bitta satr bo\'lishi kerak')
        .max(MAX_REQUISITION_LINES),
});
/** Qoralamani tahrirlash — satrlar alohida endpointlar orqali o'zgaradi. */
export const updateRequisitionSchema = z
    .object({
    purpose: z.string().min(3).max(500).optional(),
    throughPerson: z.string().min(2).max(200).nullable().optional(),
    neededBy: z.coerce.date().nullable().optional(),
    note: z.string().max(500).nullable().optional(),
})
    .refine((v) => Object.keys(v).length > 0, { message: 'O\'zgartirish uchun maydon berilmadi' });
/**
 * Ro'yxat so'rovi.
 *
 * `mine` ATAYLAB `boolean` ga o'girilmaydi (`ZodValidationPipe` sxemaning
 * kirish va chiqish turlari bir xil bo'lishini talab qiladi) — o'girish
 * kontrollerda, bir qatorda: `isMineOnly`.
 */
export const requisitionQuerySchema = paginationQuerySchema.extend({
    status: requisitionStatusSchema.optional(),
    warehouseId: idRef.optional(),
    mine: z.enum(['true', 'false']).optional(),
});
/**
 * «MENING OMBORIM» talabnomalari — sahifalash va HOLAT filtri.
 *
 * `warehouseId` va `mine` bu yerda yo'q: bo'linma bittayu bitta va uni
 * server xodimning ish joyidan oladi. Holat esa kerak — ekran «qabul
 * qilinmagan nechta?» degan savolga BUTUN ro'yxat bo'yicha javob beradi,
 * bitta sahifadan sanab emas.
 */
export const subdivisionRequisitionQuerySchema = paginationQuerySchema.extend({
    status: requisitionStatusSchema.optional(),
});
/**
 * NAVBATNING HOLAT BO'YICHA SANOG'I — yon paneldagi belgilar uchun.
 *
 * Kalitlar `RequisitionStatus`, qiymatlar — son. Nol bo'lgan holat javobga
 * UMUMAN kirmaydi: bo'sh navbatni ham sanab yuborish javobni har chaqiruvda
 * to'liq holatlar jadvaliga aylantirardi.
 */
export const inboxCountsSchema = z.object({
    total: z.number().int(),
    byStatus: z.record(requisitionStatusSchema, z.number().int()),
});
/** `?mine=true` — faqat o'z talabnomalarim. Boshqa har qanday qiymat "hammasi". */
export const isMineOnly = (raw) => raw === 'true';
/**
 * SATRNING BITTA MANZILI — «qaysi partiyadan, qaysi katakdan, qanchasi».
 *
 * Miqdor har manzilda ATAYLAB: omborda mahsulot bo'linib yotadi va bitta
 * satr bir nechta katakdan to'lishi mumkin (3 tasi bu yerdan, 1 tasi
 * undan). Ilgari manzil satrning o'zida ikkita maydon edi (`batchId`,
 * `cellId`) va bunday talabnomani skladchi umuman qabul qila olmasdi.
 */
export const requisitionLinePlacementSchema = z.object({
    batchId: z.string(),
    cellId: z.string(),
    qty: z.number(),
});
/**
 * Talabnoma satri — so'ralgani va (sklad qabul qilgandan keyin) MANZILLARI.
 *
 * `placements` bo'sh, `approvedQty` esa `null` bo'lib tug'iladi va faqat
 * skladchi qabul qilish qadamida to'ladi: aynan shular "qaysi partiyadan,
 * qaysi katakdan, qancha" degan savolga javob beradi va band qilish
 * shundan hisoblanadi. `approvedQty` — manzillardagi miqdorlar YIG'INDISI.
 */
export const requisitionLineSchema = z.object({
    id: z.string(),
    lineNo: z.number().int(),
    nomenclatureId: z.string(),
    requestedQty: z.number(),
    note: z.string().nullable(),
    placements: z.array(requisitionLinePlacementSchema),
    /**
     * KORXONA tasdiqlagan miqdor — boshliqning qarori.
     *
     * `approvedQty` bilan ikki xil savolga javob beradi: bu «shunchasiga
     * haqlisiz», `approvedQty` esa «shunchasi omborda bor». `0` — satr
     * berilmaydi, ya'ni boshliq butun hujjatni rad etmasdan bitta satrni
     * kesib tashlagan.
     */
    orgApprovedQty: z.number().nullable(),
    /** Nega kesilgani — ixtiyoriy va satrga tegishli. */
    orgNote: z.string().nullable(),
    approvedQty: z.number().nullable(),
    /**
     * HAQIQATDA berilgan miqdor — berish dalolatnomasi yozilgandan keyin to'ladi.
     *
     * Uchinchi ustun ATAYLAB: "so'radim 100 → tasdiqlandi 80 → oldim 60"
     * zanjiri to'liq saqlanadi. Ekran uni tasdiqlangan miqdordan hisoblab
     * chiqarsa, qismli berish fakti hujjatda ko'rinmay qolardi.
     */
    issuedQty: z.number().nullable(),
});
/**
 * Holat TARIXINING bitta yozuvi — "kim, qachon, qaysi holatdan qaysi holatga".
 *
 * `fromStatus` `null` — talabnomaning tug'ilishi (`DRAFT`): u o'tish emas, boshlanish.
 */
export const requisitionTransitionSchema = z.object({
    id: z.string(),
    fromStatus: requisitionStatusSchema.nullable(),
    toStatus: requisitionStatusSchema,
    actorUserId: z.string().nullable(),
    /**
     * Bajaruvchining ISMI va LAVOZIMI — yozuv bilan BIRGA keladi.
     *
     * Faqat `actorUserId` bo'lganda ekran har qadamda "Xodim #12" deb yozardi
     * yoki (yomoni) joriy foydalanuvchining ismini ko'rsatib qo'yardi —
     * timeline ning butun maqsadi esa "KIM qildi" degan savolga javob berish.
     * Ikkalasi ham `null` bo'lishi mumkin: avtomatik qadamda bajaruvchi yo'q,
     * lavozim esa xodimning aktiv ish joyi bo'lmasa noma'lum.
     */
    actorName: z.string().nullable(),
    actorPosition: z.string().nullable(),
    reason: z.string().nullable(),
    createdAt: z.string(),
});
export const requisitionSchema = z.object({
    id: z.string(),
    docNo: z.string(),
    status: requisitionStatusSchema,
    warehouseId: z.string(),
    mtuId: z.string(),
    requesterUserId: z.string(),
    requesterOrgUnitId: z.string().nullable(),
    /**
     * Korxona ICHIDAGI bo'lim — «R va AX», «TB».
     *
     * Talabnoma yozilgan paytdagi qiymat va u keyin o'zgarmaydi
     * (`requesterOrgUnitId` bilan ayni sabab). Aynan shu ustun bo'linma
     * omborini shakllantiradi: qabul tasdiqlanganda mahsulot SHU bo'linmaning
     * qoldig'iga tushadi. Bo'lim biriktirilmagan xodimda `null`.
     */
    requesterSubdivisionId: z.string().nullable(),
    purpose: z.string(),
    /** «Через кого» — mahsulotni olib ketadigan odam. */
    throughPerson: z.string().nullable(),
    neededBy: z.string().nullable(),
    note: z.string().nullable(),
    rejectReason: z.string().nullable(),
    /**
     * Talabnoma YOPILGAN payt (ISO) — `CLOSED` ga o'tganda to'ladi, undan oldin `null`.
     *
     * Tarixdan hisoblab olish mumkin edi, lekin u har ro'yxat satri uchun
     * o'tishlar zanjirini o'qishni talab qilardi: yakuniy sana hujjatning O'Z
     * maydoni bo'lishi kerak.
     */
    closedAt: z.string().nullable(),
    /**
     * SO'RALGAN payt — muddat shu kundan sanaladi.
     *
     * Ro'yxatda "necha kun qoldi" degan savolga javob berish uchun kerak:
     * muddatning o'zi yolg'iz turganda u yaqinmi yoki uzoqmi ekani
     * bilinmaydi.
     */
    createdAt: z.string(),
    /**
     * OLIB KETISHGA TAYYOR bo'lgan payt — `null` bo'lsa hali tayyor emas
     * yoki allaqachon berilgan.
     *
     * Bandlik shu paytdan sanaladi: olib ketilmagan talabnoma qoldiqni
     * ushlab turadi va "qachondan beri?" degan savolga javob kerak.
     */
    readyAt: z.string().nullable(),
});
export const requisitionWithLinesSchema = requisitionSchema.extend({
    lines: z.array(requisitionLineSchema),
    transitions: z.array(requisitionTransitionSchema),
    /**
     * SO'ROVCHINING ISMI — hujjat bilan BIRGA keladi.
     *
     * Ilgari kartochka uni o'zi topishga urinardi va topa olmasdi: xodimlar
     * ro'yxati unda yo'q, shuning uchun «Xodim #4780» deb yozilardi.
     * Hujjatning eng ko'p o'qiladigan maydonlaridan biri identifikator
     * bo'lib qolardi.
     *
     * `null` — hisob o'chirilgan. Hujjat baribir ochiladi: eski hujjatni
     * xodim ketgani uchun yo'qotib bo'lmaydi.
     */
    requesterName: z.string().nullable(),
    /** O'sha paytdagi emas, HOZIRGI lavozimi — tarixdagi bilan ayni qoida. */
    requesterPosition: z.string().nullable(),
});
/**
 * Sklad qabul qilishda satrga biriktiriladigan MANZIL.
 *
 * Talabnoma yozilayotganda partiya ham, katak ham noma'lum — ularni skladchi
 * `ORG_APPROVED → WAREHOUSE_ACCEPTED` qadamida ko'rsatadi va aynan shu qadam
 * qoldiqni band qiladi. Shuning uchun manzil boshqa hech qaysi o'tishda
 * qabul qilinmaydi.
 *
 * BITTA satr uchun bir nechta yozuv yuborilishi mumkin va bu asosiy
 * qoida: omborda mahsulot bo'linib yotadi (3 tasi bir katakda, 1 tasi
 * boshqasida) va satr shularning yig'indisi bilan to'ladi.
 *
 * `qty` — SHU manzildan olinadigan miqdor. Berilmasa satrning butun
 * chegarasi olinadi va bu faqat manzil BITTA bo'lganda ma'noga ega:
 * bir nechtasida «qolganini o'zing taqsimla» degani bo'lardi va
 * taqsimotni server o'ylab topmasligi kerak.
 */
export const requisitionPlacementSchema = z.object({
    lineId: idRef,
    batchId: idRef,
    cellId: idRef,
    qty: qtySchema.optional(),
});
/**
 * KORXONA tasdiqlashida satrga qo'yiladigan miqdor.
 *
 * `SUBMITTED → ORG_APPROVED` qadamida qabul qilinadi va boshqa hech qaysi
 * o'tishda: bu boshliqning qarori va u faqat bir marta, o'z qadamida
 * yoziladi.
 *
 * `approvedQty` bu yerda `qtySchema` EMAS — chegara `>= 0`. Nol «bu satr
 * berilmaydi» degani va u haqiqiy qaror: aks holda boshliq bitta ortiqcha
 * satr uchun butun talabnomani rad etishga majbur bo'lardi.
 */
export const requisitionApprovalSchema = z.object({
    lineId: idRef,
    approvedQty: z
        .number()
        .min(0, 'Miqdor manfiy bo\'lishi mumkin emas')
        .max(99_999_999_999, 'Miqdor juda katta'),
    note: z.string().max(500).optional(),
});
/**
 * Holatni o'zgartirish so'rovi.
 *
 * `reason` va `comment` ATAYLAB ikki xil maydon: `reason` — rad etishning
 * RASMIY sababi (hujjatga tushadi va tarixda qoladi, `hasRejectReason`
 * qoidasi uni talab qiladi), `comment` esa oddiy izoh. Bittasiga
 * birlashtirilsa, rad etish sababi ixtiyoriy izoh bo'lib qolardi.
 */
export const transitionRequisitionSchema = z.object({
    to: requisitionStatusSchema,
    comment: z.string().max(500).optional(),
    reason: z.string().max(500).optional(),
    lines: z.array(requisitionPlacementSchema).max(MAX_REQUISITION_LINES).optional(),
    /**
     * KORXONA tasdiqlagan miqdorlar — faqat `SUBMITTED → ORG_APPROVED` da.
     *
     * `lines` dan alohida maydon, chunki ular boshqa-boshqa qadamlarda va
     * boshqa-boshqa odamlar tomonidan to'ldiriladi. Bitta maydonga
     * birlashtirilsa, skladchining manzili bilan boshliqning miqdori bir
     * so'rovda kelib qolishi mumkin bo'lardi va "kim nimani hal qildi"
     * degan chegara yo'qolardi.
     */
    approvals: z.array(requisitionApprovalSchema).max(MAX_REQUISITION_LINES).optional(),
});
/**
 * Rad etish sababining eng qisqa uzunligi.
 *
 * Shartnomada turadi va IKKI tomon ham shu bitta sondan foydalanadi: dialog
 * tugmani shu chegara bilan ochadi (`RejectDialog`), state machine esa shu
 * chegara bilan o'tishni to'xtatadi (`rejectReason` sharti). Ikki joyda
 * yozilsa, ekranda ruxsat etilgan sabab serverda rad etilib qolardi.
 */
export const MIN_REJECT_REASON_LENGTH = 5;
/** Sabab haqiqiy sababmi — bo'sh joylar hisobga olinmaydi. */
export const isValidRejectReason = (reason) => reason.trim().length >= MIN_REJECT_REASON_LENGTH;
/**
 * KUTILAYOTGAN TALAB — hali band QILINMAGAN, lekin so'ralgan miqdor.
 *
 * Qoldiq bron faqat skladchi qabul qilganda qo'yiladi. Undan oldingi ikki
 * bosqichda ekran "10 ta bor" deb turaveradi va uch stansiya bir vaqtda
 * o'sha 10 tani so'rashi mumkin. Bu son shu bo'shliqni yopadi.
 *
 * BRON EMAS va shunday deb atalmaydi ham: u hech narsani qulflamaydi,
 * faqat "buni allaqachon so'rashgan" deydi. Qattiq bron qilib bo'lmaydi —
 * skladchi partiya va katakni ko'rsatmaguncha manzil yo'q.
 */
export const pendingDemandSchema = z.object({
    warehouseId: z.string(),
    nomenclatureId: z.string(),
    qty: z.number(),
});
/**
 * YETMAGAN mahsulot — `INSUFFICIENT_STOCK` javobining bitta satri.
 *
 * Uchala son ham KERAK va hech biri mijozda qayta hisoblanmaydi:
 * `requested` odam so'ragani, `free` unga haqiqatan tegadigani,
 * `pending` esa boshqa talabnomalar band qilib turgani. Ayirmani
 * frontend o'zi chiqarsa, server bilan mijoz bir xil sonni ikki xil
 * yaxlitlashi mumkin bo'lardi va oynada "6 ta yetmayapti" deb yozilib,
 * 5 ta bilan yuborilardi.
 *
 * Nomi ham shu yerda: xatolik oynasi nomenklatura so'rovini qaytadan
 * yuborishi kerak bo'lmasin — odam nimaning yetmaganini DARHOL o'qisin.
 */
export const stockShortageSchema = z.object({
    nomenclatureId: z.string(),
    nomenclatureName: z.string(),
    unit: z.string(),
    requested: z.number(),
    /** Shu odamga hozir tegadigan miqdor: mavjud qoldiq minus kutilayotgan talab. */
    free: z.number(),
    /** Boshqa talabnomalar band qilib turgani — "nega yetmadi" degan savolga javob. */
    pending: z.number(),
});
export const insufficientStockDetailsSchema = z.object({
    shortages: z.array(stockShortageSchema),
});
/**
 * Talab so'rovi.
 *
 * `nomenclatureIds` — vergul bilan ajratilgan ro'yxat. So'rov ekranda
 * KO'RINGAN mahsulotlar uchun beriladi: butun ombor talabini tortib olish
 * ekranga chiqmaydigan mingta qatorni behuda olib kelardi.
 */
export const pendingDemandQuerySchema = z.object({
    warehouseId: idRef.optional(),
    /**
     * Shu talabnomaning O'ZI sanoqdan chiqariladi.
     *
     * Boshliq tasdiqlash oynasida "boshqa talabnomalarda qancha so'ralgan"
     * degan savolga javob ko'radi. O'zi ko'rib turgan hujjat ham sanalsa,
     * son o'z-o'zini hisobga olib, "yetmayapti" deb qo'rqitardi.
     */
    excludeRequisitionId: idRef.optional(),
    nomenclatureIds: z
        .string()
        .max(4000)
        .regex(/^\d+(,\d+)*$/, 'Identifikatorlar vergul bilan ajratilgan raqamlar bo\'lishi kerak')
        .optional(),
});
/** `"1,2,3"` → `['1','2','3']`; bo'sh bo'lsa `undefined` — "hammasi". */
export const parseNomenclatureIds = (raw) => raw === undefined || raw === '' ? undefined : raw.split(',');
/**
 * O'tish javobi: yangilangan talabnoma + shu foydalanuvchi HOZIR bosa oladigan
 * keyingi holatlar.
 *
 * Ro'yxatni SERVER hisoblaydi: tugmalarni frontend o'zi hisoblasa, o'tish
 * qoidalari ikkinchi marta (va boshqacha) yozilgan bo'lardi.
 */
export const requisitionTransitionResultSchema = z.object({
    requisition: requisitionWithLinesSchema,
    availableTransitions: z.array(requisitionStatusSchema),
});
/**
 * Talabnoma KARTOCHKASI — hujjat, satrlar, tarix va shu xodim bosa oladigan
 * keyingi qadamlar.
 *
 * `availableTransitions` javobga ATAYLAB qo'shilgan: kartochka ochilganda
 * tugmalar darhol kerak, frontend esa ularni O'ZI hisoblamaydi. Hisoblasa,
 * o'tish qoidalari ikkinchi marta (va bir kun boshqacha) yozilgan bo'lardi.
 * Shakl `requisitionTransitionResultSchema` bilan bir xil ma'noga ega —
 * farqi shundaki, bu yerda ro'yxat talabnomaning O'ZI bilan bitta obyektda keladi.
 */
export const requisitionDetailSchema = requisitionWithLinesSchema.extend({
    availableTransitions: z.array(requisitionStatusSchema),
});
//# sourceMappingURL=requisition.schema.js.map